import dayjs from 'dayjs';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { type System as SystemType } from '@api/system';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { HighlightOff } from '@mui/icons-material';
import { type BillingType, CUT_FEE_MAP } from '@consts/company';
import type { Draft } from '@reduxjs/toolkit';
import { FormSelect } from '@components/forms/dropdowns/base/FormSelect';
import { PlanChangeDialog } from './PlanChangeDialog';
import { DeletePlanChangeDialog } from './DeletePlangeChangeDialog';

export interface PendingPlanChangeState {
    targetPlanId: BillingType;
    effectiveDate: string;
    justification?: string;
}

export const BillingPlan = ({
    system,
    draft,
    refetch,
    disabled,
    onSchedulePlanChange,
    pendingPlanChange,
    onCancelPending,
}: {
    system: SystemType;
    draft: [Draft<SystemType>, Dispatch<SetStateAction<Draft<SystemType>>>];
    refetch: () => void;
    disabled: boolean;
    onSchedulePlanChange: (state: PendingPlanChangeState) => void;
    pendingPlanChange: PendingPlanChangeState | null;
    onCancelPending: () => void;
}) => {
    const [planChangeDialog, setPlanChangeDialog] = useState<BillingType | null>(null);
    const [deletePlanChangeDialog, setDeletePlanChangeDialog] = useState(false);

    const handleDraftUpdate = (updateFn: (prev: Draft<SystemType>) => Draft<SystemType>) => {
        const newState = updateFn(draft[0]);
        const targetValue = newState.CutPlanID;
        setPlanChangeDialog(targetValue);
    };

    return (
        <>
            <FormSelect
                label='Billing Plan'
                disabled={Boolean(system?.planChangeRequest) || Boolean(pendingPlanChange) || disabled}
                size={12}
                draft={[draft[0], handleDraftUpdate as any]}
                stateKey='CutPlanID'
                options={Object.entries(CUT_FEE_MAP).map(([key, value]) => ({
                    label: value,
                    value: +key,
                }))}
                endAdornment={<></>}
            />

            {(pendingPlanChange || system.planChangeRequest) && (
                <Box display='flex' alignItems='center' gap={1} mt={0.5} ml={1}>
                    <Typography variant='body2'>
                        Scheduled change to{' '}
                        {CUT_FEE_MAP[(system.planChangeRequest?.CutPlanID ?? pendingPlanChange?.targetPlanId)!]} on{' '}
                        {dayjs((system.planChangeRequest?.EffectiveDate ?? pendingPlanChange?.effectiveDate)!).format(
                            'YYYY-MM-DD'
                        )}
                    </Typography>
                    {system.planChangeRequest ? (
                        <IconButton size='small' onClick={() => setDeletePlanChangeDialog(true)}>
                            <HighlightOff fontSize='small' />
                        </IconButton>
                    ) : (
                        <IconButton size='small' onClick={onCancelPending}>
                            <HighlightOff fontSize='small' />
                        </IconButton>
                    )}
                </Box>
            )}

            <PlanChangeDialog
                open={planChangeDialog !== null}
                onClose={() => setPlanChangeDialog(null)}
                onConfirm={({ effectiveDate, justification }) =>
                    onSchedulePlanChange({
                        targetPlanId: planChangeDialog!,
                        effectiveDate,
                        justification,
                    })
                }
                currentPlanId={system.CutPlanID!}
                targetPlanId={planChangeDialog!}
                stationId={system.StationID!}
            />

            {system.planChangeRequest && (
                <DeletePlanChangeDialog
                    open={deletePlanChangeDialog}
                    onClose={() => setDeletePlanChangeDialog(false)}
                    onRefresh={refetch}
                    planId={system.planChangeRequest.PlanChangeRequestID}
                />
            )}
        </>
    );
};
